import {
  CourseInviteProvider,
  CourseInvite,
  CourseOrderItem,
  CourseDiscInstUrlInfo,
  UpdateCourseDiscInstUrlInfoWithRegistration
} from 'share/domain/course'
import {
  ActiveCourse,
  getEmptyCourseInviteProvider,
  getEmptyCourseInvite,
  getEmptyCourseOrderItem,
  getEmptyDiscInstUrlInfo,
  getInactiveCourse
} from 'reducers/activecourse/activecourseSlice'

function saveCourseInviteProviderHelperToSessionStorage(
  courseInviteProvider: CourseInviteProvider,
  key: string
): void {
  const sessionStor = window.sessionStorage
  if (courseInviteProvider.orderItemId) {
    sessionStor.setItem(
      key + '-orderitemid',
      courseInviteProvider.orderItemId.toString()
    )
  }
  sessionStor.setItem(
    key + '-productkey',
    courseInviteProvider.productKey.toString()
  )
  sessionStor.setItem(key + '-name', courseInviteProvider.name)
  if (courseInviteProvider.price) {
    sessionStor.setItem(key + '-price', courseInviteProvider.price)
  }
  if (courseInviteProvider.valueProps) {
    sessionStor.setItem(key + '-valueprops', courseInviteProvider.valueProps)
  }
}

function removeCourseInviteProviderHelperFromSessionStorage(key: string) {
  const sessionStor = window.sessionStorage

  sessionStor.removeItem(key + '-orderitemid')
  sessionStor.removeItem(key + '-productkey')
  sessionStor.removeItem(key + '-name')
  sessionStor.removeItem(key + '-price')
  sessionStor.removeItem(key + '-valueprops')
}

function saveCourseInviteProvidersToSessionStorage(
  parentIndex: number,
  payload: CourseInviteProvider[] | null | undefined
): void {
  if (payload && payload.length > 0) {
    removeCourseInviteProvidersFromSessionStorage(parentIndex)
    payload.forEach((courseInviteProvider, index) => {
      const key =
        'acourse-' + parentIndex.toString() + '-invite-prov-' + index.toString()
      saveCourseInviteProviderHelperToSessionStorage(courseInviteProvider, key)
    })
  }
}

function saveSelectedCourseInviteProviderToSessionStorage(
  payload: CourseInviteProvider | null
) {
  if (payload) {
    const key = 'acourse-invite-prov-selected-'
    saveCourseInviteProviderHelperToSessionStorage(payload, key)
  }
}

function removeSelectedCourseInviteProviderFromSessionStorage(): void {
  const key = 'acourse-invite-prov-selected-'
  removeCourseInviteProviderHelperFromSessionStorage(key)
}

function removeCourseInviteProvidersFromSessionStorage(
  parentIndex: number
): void {
  for (let index = 0; index < 10; index++) {
    const key =
      'acourse-' + parentIndex.toString() + '-invite-prov-' + index.toString()
    removeCourseInviteProviderHelperFromSessionStorage(key)
  }
}

function saveCourseInvitesToSessionStorage(
  payload: CourseInvite[] | null | undefined
): void {
  const sessionStor = window.sessionStorage
  if (payload && payload.length > 0) {
    removeCourseInvitesFromSessionStorage()
    payload.forEach((courseInvite, index) => {
      const key = 'acourse-invite' + index.toString()
      sessionStor.setItem(key + '-coursenumber', courseInvite.courseNumber)
      sessionStor.setItem(key + '-orderid', courseInvite.orderId.toString())
      sessionStor.setItem(
        key + '-coursepaymenttype',
        courseInvite.coursePaymentType.toString()
      )
      if (
        courseInvite.courseProviders &&
        courseInvite.courseProviders.length > 0
      ) {
        saveCourseInviteProvidersToSessionStorage(
          index,
          courseInvite.courseProviders
        )
      }
    })
  }
}

function removeCourseInvitesFromSessionStorage(): void {
  const sessionStor = window.sessionStorage
  for (let index = 0; index < 10; index++) {
    const key = 'acourse-invite' + index.toString()
    const keyCourseNumber = key + '-coursenumber'
    const keyOrderId = key + '-orderid'
    const keyCoursePaymentType = key + '-coursepaymenttype'

    sessionStor.removeItem(keyCourseNumber)
    sessionStor.removeItem(keyOrderId)
    sessionStor.removeItem(keyCoursePaymentType)
    removeCourseInviteProvidersFromSessionStorage(index)
  }
}

function saveCourseOrderItemsToSessionStorage(
  payload: CourseOrderItem[] | null | undefined
): void {
  const sessionStor = window.sessionStorage
  if (payload && payload.length > 0) {
    removeCourseOrderItemsFromSessionStorage()
    payload.forEach((courseOrderItem, index) => {
      const key = 'acourse-orderitem' + index.toString()
      sessionStor.setItem(key + '-coursenumber', courseOrderItem.courseNumber)
      sessionStor.setItem(key + '-orderid', courseOrderItem.orderId.toString())
      sessionStor.setItem(
        key + '-orderitemid',
        courseOrderItem.orderItemId.toString()
      )
      sessionStor.setItem(key + '-isjoint', courseOrderItem.isJoint.toString())
      sessionStor.setItem(
        key + '-providerid',
        courseOrderItem.providerId.toString()
      )
      sessionStor.setItem(key + '-providername', courseOrderItem.providerName)
      sessionStor.setItem(
        key + '-coursetype',
        courseOrderItem.courseType.toString()
      )
      sessionStor.setItem(
        key + '-coursestatus',
        courseOrderItem.courseStatus.toString()
      )
      sessionStor.setItem(
        key + '-coursemethod',
        courseOrderItem.courseMethod.toString()
      )
      sessionStor.setItem(
        key + '-coursepaymenttype',
        courseOrderItem.coursePaymentType.toString()
      )
      if (courseOrderItem.coursePrice) {
        sessionStor.setItem(key + '-courseprice', courseOrderItem.coursePrice)
      } else {
        sessionStor.removeItem(key + '-courseprice')
      }
      if (courseOrderItem.coursePhoneNumber) {
        sessionStor.setItem(
          key + '-coursephonenumber',
          courseOrderItem.coursePhoneNumber
        )
      } else {
        sessionStor.removeItem(key + '-coursephonenumber')
      }
      if (courseOrderItem.courseAccessCode) {
        sessionStor.setItem(
          key + '-courseaccesscode',
          courseOrderItem.courseAccessCode
        )
      } else {
        sessionStor.removeItem(key + '-courseaccesscode')
      }
      if (courseOrderItem.courseCompletionDate) {
        sessionStor.setItem(
          key + '-coursecompletiondate',
          courseOrderItem.courseCompletionDate
        )
      } else {
        sessionStor.removeItem(key + '-coursecompletiondate')
      }
      if (courseOrderItem.courseCertificationExpirationDate) {
        sessionStor.setItem(
          key + '-coursecertificationexpirationdate',
          courseOrderItem.courseCertificationExpirationDate
        )
      } else {
        sessionStor.removeItem(key + '-coursecertificationexpirationdate')
      }
      sessionStor.setItem(
        key + '-verifieddetails',
        courseOrderItem.verifiedDetails.toString()
      )
      sessionStor.setItem(
        key + '-verifiedcodebtordetails',
        courseOrderItem.verifiedCoDebtorDetails.toString()
      )
      sessionStor.setItem(
        key + '-accepteddisclosureagreement',
        courseOrderItem.acceptedDisclosureAgreement.toString()
      )
      sessionStor.setItem(
        key + '-showncourseinstructions',
        courseOrderItem.shownCourseInstructions.toString()
      )
    })
  }
}

function removeCourseOrderItemsFromSessionStorage(): void {
  const sessionStor = window.sessionStorage
  for (let index = 0; index < 10; index++) {
    const key = 'acourse-orderitem' + index.toString()
    sessionStor.removeItem(key + '-coursenumber')
    sessionStor.removeItem(key + '-orderid')
    sessionStor.removeItem(key + '-orderitemid')
    sessionStor.removeItem(key + '-isjoint')
    sessionStor.removeItem(key + '-providerid')
    sessionStor.removeItem(key + '-providername')
    sessionStor.removeItem(key + '-coursetype')
    sessionStor.removeItem(key + '-coursestatus')
    sessionStor.removeItem(key + '-coursemethod')
    sessionStor.removeItem(key + '-coursepaymenttype')
    sessionStor.removeItem(key + '-courseprice')
    sessionStor.removeItem(key + '-coursephonenumber')
    sessionStor.removeItem(key + '-courseaccesscode')
    sessionStor.removeItem(key + '-coursecompletiondate')
    sessionStor.removeItem(key + '-coursecertificationexpirationdate')
    sessionStor.removeItem(key + '-verifieddetails')
    sessionStor.removeItem(key + '-verifiedcodebtordetails')
    sessionStor.removeItem(key + '-accepteddisclosureagreement')
    sessionStor.removeItem(key + '-showncourseinstructions')
  }
}

function saveCourseDiscInstUrlInfoToSessionStorage(
  payload: CourseDiscInstUrlInfo[] | null | undefined
): void {
  const sessionStor = window.sessionStorage
  if (payload && payload.length > 0) {
    removeCourseDiscInstUrlInfoFromSessionStorage()
    payload.forEach((courseDiscInstUrlInfo, index) => {
      const key = 'acourse-discinsturlinfo' + index.toString()
      sessionStor.setItem(
        key + '-coursenumber',
        courseDiscInstUrlInfo.courseNumber
      )
      sessionStor.setItem(
        key + '-courseorderitemId',
        courseDiscInstUrlInfo.courseOrderItemId.toString()
      )
      sessionStor.setItem(key + '-courseurl', courseDiscInstUrlInfo.courseUrl)
      sessionStor.setItem(
        key + '-instructions',
        courseDiscInstUrlInfo.instructions
      )
      sessionStor.setItem(
        key + '-disclosurecontent',
        courseDiscInstUrlInfo.disclosureContent
      )
      sessionStor.setItem(
        key + '-disclosureversion',
        courseDiscInstUrlInfo.disclosureVersion.toString()
      )
      if (courseDiscInstUrlInfo.info1) {
        sessionStor.setItem(
          key + '-info1',
          courseDiscInstUrlInfo.info1.toString()
        )
      } else {
        sessionStor.removeItem(key + '-info1')
      }

      if (courseDiscInstUrlInfo.info2) {
        sessionStor.setItem(
          key + '-info2',
          courseDiscInstUrlInfo.info2.toString()
        )
      } else {
        sessionStor.removeItem(key + '-info2')
      }
    })
  }
}

function removeCourseDiscInstUrlInfoFromSessionStorage(): void {
  const sessionStor = window.sessionStorage
  for (let index = 0; index < 10; index++) {
    const key = 'acourse-discinsturlinfo' + index.toString()
    sessionStor.removeItem(key + '-coursenumber')
    sessionStor.removeItem(key + '-courseorderitemId')
    sessionStor.removeItem(key + '-courseurl')
    sessionStor.removeItem(key + '-instructions')
    sessionStor.removeItem(key + '-disclosurecontent')
    sessionStor.removeItem(key + '-disclosureversion')
    sessionStor.removeItem(key + '-info1')
    sessionStor.removeItem(key + '-info2')
  }
}

export function saveCourseInvitesToSessionStorageMain(
  payload: CourseInvite[]
): void {
  if (payload && payload.length > 0) {
    saveCourseInvitesToSessionStorage(payload)
  } else {
    removeCourseInvitesFromSessionStorage()
  }
}

export function saveCourseOrderItemsToSessionStorageMain(
  payload: CourseOrderItem[]
): void {
  if (payload && payload.length > 0) {
    saveCourseOrderItemsToSessionStorage(payload)
  } else {
    removeCourseOrderItemsFromSessionStorage()
  }
}

export function saveCourseDiscInstUrlInfoToSessionStorageMain(
  payload: CourseDiscInstUrlInfo[]
): void {
  if (payload && payload.length > 0) {
    saveCourseDiscInstUrlInfoToSessionStorage(payload)
  } else {
    removeCourseDiscInstUrlInfoFromSessionStorage()
  }
}

export function saveSelectedCourseToSessionStorageMain(
  payload: CourseInviteProvider | null
): void {
  if (payload) {
    saveSelectedCourseInviteProviderToSessionStorage(payload)
  } else {
    removeSelectedCourseInviteProviderFromSessionStorage()
  }
}

export function saveIsActiveCourseToSessionStorage(payload: boolean): void {
  const sessionStor = window.sessionStorage
  sessionStor.setItem('acourse-isactivecourse', payload.toString())
}

export function saveTitleToSessionStorage(payload: string | null): void {
  const sessionStor = window.sessionStorage
  if (payload) {
    sessionStor.setItem('acourse-title', payload)
  } else {
    sessionStor.removeItem('acourse-title')
  }
}

export function saveErrorMessageToSessionStorage(payload: string | null): void {
  const sessionStor = window.sessionStorage
  if (payload) {
    sessionStor.setItem('acourse-errormessage', payload)
  } else {
    sessionStor.removeItem('acourse-errormessage')
  }
}

export function saveActiveTabToSessionStorage(
  payload: 'first' | 'second'
): void {
  const sessionStor = window.sessionStorage
  sessionStor.setItem('acourse-activetab', payload)
}

export function saveVerifiedDetailsTrueToSessionStorage(index: number): void {
  const sessionStor = window.sessionStorage
  const key = 'acourse-orderitem' + index.toString()
  sessionStor.setItem(key + '-verifieddetails', 'true')
}

export function saveVerifiedCoDebtorDetailsTrueToSessionStorage(
  index: number
): void {
  const sessionStor = window.sessionStorage
  const key = 'acourse-orderitem' + index.toString()
  sessionStor.setItem(key + '-verifiedcodebtordetails', 'true')
}

export function saveAcceptedDisclosureAgreementTrueToSessionStorage(
  index: number
): void {
  const sessionStor = window.sessionStorage
  const key = 'acourse-orderitem' + index.toString()
  sessionStor.setItem(key + '-accepteddisclosureagreement', 'true')
}

export function savehownCourseInstructionsTrueToSessionStorage(
  index: number
): void {
  const sessionStor = window.sessionStorage
  const key = 'acourse-orderitem' + index.toString()
  sessionStor.setItem(key + '-showncourseinstructions', 'true')
}

export function saveUpdateCourseDiscInstUrlInfoFieldsToSessionStorage(
  index: number,
  payload: UpdateCourseDiscInstUrlInfoWithRegistration
): void {
  const sessionStor = window.sessionStorage
  const key = 'acourse-discinsturlinfo' + index.toString()
  sessionStor.setItem(key + '-courseurl', payload.courseUrl)
  if (payload.info1) {
    sessionStor.setItem(key + '-info1', payload.info1.toString())
  } else {
    sessionStor.removeItem(key + '-info1')
  }

  if (payload.info2) {
    sessionStor.setItem(key + '-info2', payload.info2.toString())
  } else {
    sessionStor.removeItem(key + '-info2')
  }
}

export function saveActiveCourseToSessionStorage(payload: ActiveCourse): void {
  if (payload) {
    saveIsActiveCourseToSessionStorage(payload.isActiveCourse)
    saveTitleToSessionStorage(payload.title)
    saveErrorMessageToSessionStorage(payload.errorMessage)
    saveActiveTabToSessionStorage(payload.activeTab)
    saveCourseInvitesToSessionStorageMain(payload.inviteData)
    saveCourseOrderItemsToSessionStorageMain(payload.orderData)
    saveCourseDiscInstUrlInfoToSessionStorageMain(payload.discInstUrlInfo)
    saveSelectedCourseToSessionStorageMain(payload.selectedCourse)
  }
}

// session storage methods load methods
function loadCourseInviteProviderHelperFromSessionStorage(
  key: string
): CourseInviteProvider | null {
  const sessionStor = window.sessionStorage
  const courseInviteProvider = getEmptyCourseInviteProvider()
  let hasValue = false

  const orderItemId = sessionStor.getItem(key + '-orderitemid')
  if (orderItemId) {
    courseInviteProvider.orderItemId = parseInt(orderItemId)
    hasValue = true
  }

  const productKey = sessionStor.getItem(key + '-productkey')
  if (productKey) {
    courseInviteProvider.productKey = parseInt(productKey)
    hasValue = true
  }

  const name = sessionStor.getItem(key + '-name')
  if (name) {
    courseInviteProvider.name = name
    hasValue = true
  }

  const price = sessionStor.getItem(key + '-price')
  if (price) {
    courseInviteProvider.price = price
    hasValue = true
  }

  const valueProps = sessionStor.getItem(key + '-valueprops')
  if (valueProps) {
    courseInviteProvider.valueProps = valueProps
    hasValue = true
  }

  if (hasValue) {
    return courseInviteProvider
  } else {
    return null
  }
}

function loadCourseInviteProvidersFromSessionStorage(
  parentIndex: number
): CourseInviteProvider[] {
  const courseInviteProviders: CourseInviteProvider[] = []
  for (let index = 0; index < 10; index++) {
    const key =
      'acourse-' + parentIndex.toString() + '-invite-prov-' + index.toString()
    const courseInviteProvider =
      loadCourseInviteProviderHelperFromSessionStorage(key)
    if (courseInviteProvider) {
      courseInviteProviders.push(courseInviteProvider)
    }
  }
  return courseInviteProviders
}

function loadSelectedCourseInviteProviderFromSessionStorage(): CourseInviteProvider | null {
  const key = 'acourse-invite-prov-selected-'
  const courseInviteProvider =
    loadCourseInviteProviderHelperFromSessionStorage(key)
  return courseInviteProvider
}

function loadCourseInvitesFromSessionStorage(): CourseInvite[] {
  const sessionStor = window.sessionStorage
  const courseInvites: CourseInvite[] = []
  for (let index = 0; index < 10; index++) {
    const courseInvite = getEmptyCourseInvite()
    const key = 'acourse-invite' + index.toString()
    let hasValue = false

    const courseNumber = sessionStor.getItem(key + '-coursenumber')
    if (
      courseNumber &&
      (courseNumber === 'first' || courseNumber === 'second')
    ) {
      courseInvite.courseNumber = courseNumber
      hasValue = true
    }

    const orderId = sessionStor.getItem(key + '-orderid')
    if (orderId) {
      courseInvite.orderId = parseInt(orderId)
      hasValue = true
    }

    const coursePaymentType = sessionStor.getItem(key + '-coursepaymenttype')
    if (coursePaymentType) {
      courseInvite.coursePaymentType = parseInt(coursePaymentType)
      hasValue = true
    }

    const courseProviders = loadCourseInviteProvidersFromSessionStorage(index)
    if (courseProviders.length > 0) {
      courseInvite.courseProviders = courseProviders
      hasValue = true
    }

    if (hasValue) {
      courseInvites.push(courseInvite)
    }
  }
  return courseInvites
}

function loadCourseOrderItemFromSessionStorage(
  key: string
): CourseOrderItem | null {
  const sessionStor = window.sessionStorage
  const courseOrderItem = getEmptyCourseOrderItem()
  let hasValue = false

  const courseNumber = sessionStor.getItem(key + '-coursenumber')
  if (courseNumber && (courseNumber === 'first' || courseNumber === 'second')) {
    courseOrderItem.courseNumber = courseNumber
    hasValue = true
  }

  const orderId = sessionStor.getItem(key + '-orderid')
  if (orderId) {
    courseOrderItem.orderId = parseInt(orderId)
    hasValue = true
  }

  const orderItemId = sessionStor.getItem(key + '-orderitemid')
  if (orderItemId) {
    courseOrderItem.orderItemId = parseInt(orderItemId)
    hasValue = true
  }

  const isJoint = sessionStor.getItem(key + '-isjoint')
  if (isJoint) {
    courseOrderItem.isJoint = isJoint === 'true'
    hasValue = true
  }

  const providerId = sessionStor.getItem(key + '-providerid')
  if (providerId) {
    courseOrderItem.providerId = parseInt(providerId)
    hasValue = true
  }

  const providerName = sessionStor.getItem(key + '-providername')
  if (providerName) {
    courseOrderItem.providerName = providerName
    hasValue = true
  }

  const courseType = sessionStor.getItem(key + '-coursetype')
  if (courseType) {
    courseOrderItem.courseType = parseInt(courseType)
    hasValue = true
  }

  const courseStatus = sessionStor.getItem(key + '-coursestatus')
  if (courseStatus) {
    courseOrderItem.courseStatus = parseInt(courseStatus)
    hasValue = true
  }

  const courseMethod = sessionStor.getItem(key + '-coursemethod')
  if (courseMethod) {
    courseOrderItem.courseMethod = parseInt(courseMethod)
    hasValue = true
  }

  const coursePaymentType = sessionStor.getItem(key + '-coursepaymenttype')
  if (coursePaymentType) {
    courseOrderItem.coursePaymentType = parseInt(coursePaymentType)
    hasValue = true
  }

  const coursePrice = sessionStor.getItem(key + '-courseprice')
  if (coursePrice) {
    courseOrderItem.coursePrice = coursePrice
    hasValue = true
  }

  const coursePhoneNumber = sessionStor.getItem(key + '-coursephonenumber')
  if (coursePhoneNumber) {
    courseOrderItem.coursePhoneNumber = coursePhoneNumber
    hasValue = true
  }

  const courseAccessCode = sessionStor.getItem(key + '-courseaccesscode')
  if (courseAccessCode) {
    courseOrderItem.courseAccessCode = courseAccessCode
    hasValue = true
  }

  const courseCompletionDate = sessionStor.getItem(
    key + '-coursecompletiondate'
  )
  if (courseCompletionDate) {
    courseOrderItem.courseCompletionDate = courseCompletionDate
    hasValue = true
  }

  const courseCertificationExpirationDate = sessionStor.getItem(
    key + '-coursecertificationexpirationdate'
  )
  if (courseCertificationExpirationDate) {
    courseOrderItem.courseCertificationExpirationDate =
      courseCertificationExpirationDate
    hasValue = true
  }

  const verifiedDetails = sessionStor.getItem(key + '-verifieddetails')
  if (verifiedDetails) {
    courseOrderItem.verifiedDetails = verifiedDetails === 'true'
    hasValue = true
  }

  const verifiedCoDebtorDetails = sessionStor.getItem(
    key + '-verifiedcodebtordetails'
  )
  if (verifiedCoDebtorDetails) {
    courseOrderItem.verifiedCoDebtorDetails = verifiedCoDebtorDetails === 'true'
    hasValue = true
  }

  const acceptedDisclosureAgreement = sessionStor.getItem(
    key + '-accepteddisclosureagreement'
  )
  if (acceptedDisclosureAgreement) {
    courseOrderItem.acceptedDisclosureAgreement =
      acceptedDisclosureAgreement === 'true'
    hasValue = true
  }

  const shownCourseInstructions = sessionStor.getItem(
    key + '-showncourseinstructions'
  )
  if (shownCourseInstructions) {
    courseOrderItem.shownCourseInstructions = shownCourseInstructions === 'true'
    hasValue = true
  }

  if (hasValue) {
    return courseOrderItem
  } else {
    return null
  }
}

function loadOrderItemsFromSessionStorage(): CourseOrderItem[] {
  const courseOrderItems: CourseOrderItem[] = []
  for (let index = 0; index < 10; index++) {
    const key = 'acourse-orderitem' + index.toString()
    const courseOrderItem = loadCourseOrderItemFromSessionStorage(key)
    if (courseOrderItem) {
      courseOrderItems.push(courseOrderItem)
    }
  }
  return courseOrderItems
}

function loadCourseDiscInstUrlInfoFromSessionStorage(
  key: string
): CourseDiscInstUrlInfo | null {
  const sessionStor = window.sessionStorage
  const courseDiscInstUrlInfo = getEmptyDiscInstUrlInfo()
  let hasValue = false

  const courseNumber = sessionStor.getItem(key + '-coursenumber')
  if (courseNumber && (courseNumber === 'first' || courseNumber === 'second')) {
    courseDiscInstUrlInfo.courseNumber = courseNumber
    hasValue = true
  }

  const courseOrderItemId = sessionStor.getItem(key + '-courseorderitemId')
  if (courseOrderItemId) {
    courseDiscInstUrlInfo.courseOrderItemId = parseInt(courseOrderItemId)
    hasValue = true
  }

  const courseUrl = sessionStor.getItem(key + '-courseurl')
  if (courseUrl) {
    courseDiscInstUrlInfo.courseUrl = courseUrl
    hasValue = true
  }

  const instructions = sessionStor.getItem(key + '-instructions')
  if (instructions) {
    courseDiscInstUrlInfo.instructions = instructions
    hasValue = true
  }

  const disclosureContent = sessionStor.getItem(key + '-disclosurecontent')
  if (disclosureContent) {
    courseDiscInstUrlInfo.disclosureContent = disclosureContent
    hasValue = true
  }

  const disclosureVersion = sessionStor.getItem(key + '-disclosureversion')
  if (disclosureVersion) {
    courseDiscInstUrlInfo.disclosureVersion = parseInt(disclosureVersion)
    hasValue = true
  }

  if (hasValue) {
    return courseDiscInstUrlInfo
  } else {
    return null
  }
}

function loadCourseDiscInstUrlInfosFromSessionStorage(): CourseDiscInstUrlInfo[] {
  const courseDiscInstUrlInfos: CourseDiscInstUrlInfo[] = []
  for (let index = 0; index < 10; index++) {
    const key = 'acourse-discinsturlinfo' + index.toString()
    const courseDiscInstUrlInfo =
      loadCourseDiscInstUrlInfoFromSessionStorage(key)
    if (courseDiscInstUrlInfo) {
      courseDiscInstUrlInfos.push(courseDiscInstUrlInfo)
    }
  }
  return courseDiscInstUrlInfos
}

export function loadActiveCourseFromSessionStorage(): ActiveCourse {
  const sessionStor = window.sessionStorage
  const activeCourse = getInactiveCourse()

  const isActiveCourse = sessionStor.getItem('acourse-isactivecourse')
  if (isActiveCourse) {
    activeCourse.isActiveCourse = isActiveCourse === 'true'
  }

  const title = sessionStor.getItem('acourse-title')
  if (title) {
    activeCourse.title = title
  }

  const errorMessage = sessionStor.getItem('acourse-errormessage')
  if (errorMessage) {
    activeCourse.errorMessage = errorMessage
  }

  const activeTab = sessionStor.getItem('acourse-activetab')
  if (activeTab && (activeTab === 'first' || activeTab === 'second')) {
    activeCourse.activeTab = activeTab
  }

  activeCourse.inviteData = loadCourseInvitesFromSessionStorage()
  activeCourse.orderData = loadOrderItemsFromSessionStorage()
  activeCourse.discInstUrlInfo = loadCourseDiscInstUrlInfosFromSessionStorage()

  activeCourse.selectedCourse =
    loadSelectedCourseInviteProviderFromSessionStorage()

  return activeCourse
}
